/**
 * CameraDevicePicker — robust cross-device camera + mic selection
 * Works on: Windows / macOS / Linux laptops, iPhone (front + back), Android
 * Handles: HTTPS / localhost gating, permission denials, multi-camera setups,
 *          USB webcam hotplug, browser quirks (Safari, Chrome, Firefox).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Mic, RefreshCw, AlertTriangle, Check, Smartphone } from 'lucide-react';

export const CameraDevicePicker = ({
  onStreamReady,            // (stream, deviceInfo) => void
  onError,                  // (errMessage) => void
  videoConstraints = { width: { ideal: 1280 }, height: { ideal: 720 } },
  audioEnabled = true,
  autoStart = true,
  showFlipButton = true,    // mobile front/back flip
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [selectedAudio, setSelectedAudio] = useState('');
  const [facingMode, setFacingMode] = useState('user'); // 'user' | 'environment'
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');
  const [permissionState, setPermissionState] = useState('prompt'); // prompt | granted | denied
  const [isMobile, setIsMobile] = useState(false);
  const [isHTTPS, setIsHTTPS] = useState(true);

  // Check environment
  useEffect(() => {
    const ua = navigator.userAgent || '';
    setIsMobile(/Mobi|Android|iPhone|iPad|iPod/i.test(ua));
    setIsHTTPS(window.location.protocol === 'https:' || window.location.hostname === 'localhost');
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Enumerate available devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter(d => d.kind === 'videoinput');
      const audios = devices.filter(d => d.kind === 'audioinput');
      setVideoDevices(videos);
      setAudioDevices(audios);
      if (!selectedVideo && videos.length > 0) setSelectedVideo(videos[0].deviceId);
      if (!selectedAudio && audios.length > 0) setSelectedAudio(audios[0].deviceId);
      return { videos, audios };
    } catch (e) {
      return { videos: [], audios: [] };
    }
  }, [selectedVideo, selectedAudio]);

  // Start camera with specified device
  const startCamera = useCallback(async (videoId, audioId, mode) => {
    if (!isHTTPS) {
      const msg = 'Camera access requires HTTPS. Please use a secure connection.';
      setError(msg); onError?.(msg); return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      const msg = 'Your browser does not support camera access. Try Chrome, Edge, Safari, or Firefox.';
      setError(msg); onError?.(msg); return;
    }
    setIsStarting(true); setError('');
    stopStream();
    try {
      const constraints = {
        video: {
          ...videoConstraints,
          ...(videoId ? { deviceId: { exact: videoId } } : { facingMode: mode || facingMode }),
        },
        audio: audioEnabled ? (audioId ? { deviceId: { exact: audioId } } : true) : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setPermissionState('granted');
      // After permissions, labels appear → re-enumerate
      const { videos } = await enumerateDevices();
      // Find which device the browser actually picked
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings?.() || {};
      const actualId = settings.deviceId || videoId;
      if (actualId) setSelectedVideo(actualId);
      onStreamReady?.(stream, { deviceId: actualId, label: videos.find(v => v.deviceId === actualId)?.label || track?.label });
    } catch (e) {
      let msg = 'Unable to access camera/microphone.';
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        msg = 'Permission denied. Please allow camera & microphone in your browser settings, then click Retry.';
        setPermissionState('denied');
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        msg = 'No camera found. Please connect a webcam or use a device with a built-in camera.';
      } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        msg = 'Camera is in use by another application. Close other video apps and try again.';
      } else if (e.name === 'OverconstrainedError') {
        msg = 'Selected camera does not support the requested settings. Pick a different one.';
      } else if (e.name === 'SecurityError') {
        msg = 'Browser blocked camera access. Ensure the page is loaded over HTTPS.';
      }
      setError(msg); onError?.(msg);
    } finally {
      setIsStarting(false);
    }
  }, [isHTTPS, videoConstraints, audioEnabled, facingMode, stopStream, enumerateDevices, onStreamReady, onError]);

  // Auto-start once on mount
  useEffect(() => {
    if (autoStart) {
      enumerateDevices().then(() => startCamera('', '', 'user'));
    }
    return () => stopStream();
  }, []);  // eslint-disable-line

  // Listen for hotplug
  useEffect(() => {
    const handler = () => enumerateDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', handler);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', handler);
  }, [enumerateDevices]);

  const handleVideoChange = (id) => { setSelectedVideo(id); startCamera(id, selectedAudio, facingMode); };
  const handleAudioChange = (id) => { setSelectedAudio(id); startCamera(selectedVideo, id, facingMode); };
  const handleFlip = () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    startCamera('', selectedAudio, next);  // facingMode requires no deviceId
  };

  return (
    <div className="space-y-3" data-testid="camera-device-picker">
      {/* Live preview */}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-preview" />
        {isStarting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Starting camera…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 text-white p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-400 mb-2" />
            <div className="text-sm font-semibold mb-3">{error}</div>
            <button onClick={() => startCamera(selectedVideo, selectedAudio, facingMode)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg text-sm font-semibold flex items-center gap-2"
              data-testid="camera-retry-btn">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        )}
        {!error && !isStarting && permissionState === 'granted' && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-500/90 text-white rounded-md text-[10px] font-bold flex items-center gap-1">
            <Check className="w-3 h-3" /> LIVE
          </div>
        )}
        {showFlipButton && isMobile && permissionState === 'granted' && (
          <button onClick={handleFlip}
            className="absolute bottom-2 right-2 p-2.5 bg-white/20 hover:bg-white/30 backdrop-blur rounded-full"
            data-testid="camera-flip-btn" aria-label="Flip camera">
            <Smartphone className="w-4 h-4 text-white" />
          </button>
        )}
      </div>

      {/* Device pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <Camera className="w-3 h-3" /> Camera
          </label>
          <select value={selectedVideo} onChange={e => handleVideoChange(e.target.value)}
            disabled={isStarting || videoDevices.length === 0}
            data-testid="camera-select"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-300 outline-none disabled:opacity-50">
            {videoDevices.length === 0 && <option>No cameras detected</option>}
            {videoDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Camera ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
        {audioEnabled && (
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <Mic className="w-3 h-3" /> Microphone
            </label>
            <select value={selectedAudio} onChange={e => handleAudioChange(e.target.value)}
              disabled={isStarting || audioDevices.length === 0}
              data-testid="mic-select"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-300 outline-none disabled:opacity-50">
              {audioDevices.length === 0 && <option>No microphones detected</option>}
              {audioDevices.map((d, i) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `Microphone ${i + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!isHTTPS && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Cameras require HTTPS. The current page is served over HTTP — switch to https://infuseai.net to enable video.
        </div>
      )}
    </div>
  );
};

export default CameraDevicePicker;
